import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { Cell } from 'react-foundation';
import { useFormikContext, useField } from 'formik';
import UserContext from '../../UserContext';
import { ApiFetch } from '../Common/Api';
import { SaveEvent, CheckboxEvent } from '../Common/AnalyticsEvents';

export type SpecialtyCategories = {
  key: string;
  value: string;
  extended: string;
  isSelected: boolean;
};

type ServiceTypesProps = {
  allowedSpecialtyCategories?: string[];
};

const ServiceTypes: React.FC<ServiceTypesProps> = ({ allowedSpecialtyCategories }) => {
  // Code for Bid Categories have been commented out until we have further confirmation on bids 
  // Constants for field and lookup
  const name = "specialtyCategories";
  const lookupKey = "SpecialtyCategories";
  const noLabel = true;

  const initialized = useRef(false);
  const user = useContext(UserContext);
  const { cachedGet } = user;
  const [list, setList] = useState<SpecialtyCategories[]>([]);
  const { setFieldValue } = useFormikContext();
  const [field, { value: values }] = useField<string[] | undefined>(name);
  const { CacheLookupsMinutes } = window.CS_CONFIG;
  const normalizedAllowedCategories = useMemo(
    () => new Set(
      (allowedSpecialtyCategories ?? [])
        .filter((category): category is string => !!category)
        .map(category => category.trim().toLowerCase())
    ),
    [allowedSpecialtyCategories]
  );
  const isCategoryAllowed = (category: string) =>
    normalizedAllowedCategories.has((category ?? '').trim().toLowerCase());

  useEffect(() => {
    cachedGet('ApiLookup' + lookupKey, CacheLookupsMinutes, () =>
      ApiFetch<SpecialtyCategories[]>('Lookup', { key: lookupKey })
    )
      .then((result) => {
        const categories = result ?? [];
        setList(categories);
        if (!initialized.current) {
          const selected = categories
            .filter(o => o.isSelected && normalizedAllowedCategories.has((o.value ?? '').trim().toLowerCase()))
            .map(o => o.value);
          setFieldValue(name, selected);
          initialized.current = true;
        }
      })
      .catch(console.log);
  }, [lookupKey, cachedGet, CacheLookupsMinutes, name, normalizedAllowedCategories, setFieldValue]);

  const groupedList = useMemo(() => {
    return list.reduce((acc, item) => {
      if (!acc[item.key]) acc[item.key] = [];
      acc[item.key].push(item);
      return acc;
    }, {} as Record<string, SpecialtyCategories[]>);
  }, [list]);


  const handleChange = (checked: boolean, optionValue: string) => {
    if (checked) {
      setFieldValue(name, [...(values ?? []), optionValue]);
    } else {
      setFieldValue(name, (values ?? []).filter((v) => v !== optionValue));
    }
    SaveEvent(CheckboxEvent(user, 'Service Types', optionValue, checked));
  };

  return (
    <Cell className="card service-types-card">
      <Cell>
        <h6 className="margin-none color-black preferences-section-title">
          <span className="far fa-suitcase margin-right"></span>
          <b>Service Categories</b>
        </h6>
        <p className="font-small color-black">
          Select the services you provide to receive relevant postings
        </p>
      </Cell>
      <Cell small={12} className="checkbox-flex full-width service-types-list">
        <>
          {!noLabel && (
            <label htmlFor={field.name}></label>
          )}

          {/* Render SpecialtyBidCategories group */}
          {/* {groupedList['SpecialtyBidsCategories'] && (
            <div>
              <h6>Modifications/Equipment/Devices</h6>
              {groupedList['SpecialtyBidsCategories'].map((o, i) => (
                <label key={'bid-' + i} className="custom-checkbox-label">
                  <input
                    type="checkbox"
                    name={field.name}
                    value={o.value}
                    checked={Array.isArray(values) ? values.includes(o.value) : false}
                    onChange={e => handleChange(e.target.checked, o.value)}
                    disabled={!isCategoryAllowed(o.value)}
                    className="custom-checkbox-input"
                  />
                  <span className="custom-checkbox"></span>
                  {o.value}
                </label>
              ))}
            </div>
          )} */}

          {/* Render SpecialtyServicesCategories group */}
          {groupedList['SpecialtyServicesCategories'] && (
            <div>
              {groupedList['SpecialtyServicesCategories'].map((o, i) => 
                <label key={'service-' + i} className="custom-checkbox-label">
                  <input
                    type="checkbox"
                    name={field.name}
                    value={o.value}
                    checked={Array.isArray(values) ? values.includes(o.value) : false}
                    onChange={e => handleChange(e.target.checked, o.value)}
                    disabled={!isCategoryAllowed(o.value)}
                    className="custom-checkbox-input"
                  />
                  <span className="custom-checkbox"></span>
                  {o.value}
                </label>
              )}
            </div>
          )}
        </>
      </Cell>
    </Cell>
  );
};

export default ServiceTypes;
